import {Iterable, List} from 'immutable';

function deepFind(iterable: Iterable<unknown, unknown>, pathToProperty: unknown[] = []): string {
  const match = pathToProperty.reduce<Iterable<unknown, unknown>>((entries, key) => {
    return entries && entries.get(key) ? (entries.get(key) as Iterable<unknown, unknown>) : List();
  }, iterable);
  if (match.some((entry) => typeof entry !== 'string')) {
    logger.warn('At least one nested entry found at path provided. Try a more specific path.');
  }
  return List(match)
    .flatten()
    .join('');
}

/**
 * Validator methods are bound dynamically onto each `Validators` instance (see
 * `Generic.model` `__bindValidatorsToInstance`). Each returns either an Immutable
 * collection of error messages or `null` when the field is valid.
 */
type ValidatorFn = () => Iterable<unknown, unknown> | List<string> | null;

export class Validators {
  getValidationErrors({
    skip = []
  }: {
    skip?: string[];
  } = {}): List<string> | null {
    const validators = this as unknown as Record<string, ValidatorFn>;
    return Object.keys(this).reduce<List<string> | null>((errors, method) => {
      if (skip.includes(method)) {
        return errors;
      }
      const result = validators[method]();
      const fieldErrors: List<string> | null = Iterable.isIterable(result)
        ? ((result as Iterable<unknown, unknown>).toList().flatten() as List<string>)
        : (result as List<string> | null);
      if (fieldErrors !== null) {
        return errors ? (errors.concat(fieldErrors) as List<string>) : fieldErrors;
      }
      return errors;
    }, null);
  }

  hasValidationErrors({
    skip = []
  }: {
    skip?: string[];
  } = {}): boolean {
    const validators = this as unknown as Record<string, ValidatorFn>;
    return Object.keys(this).some((method) => {
      if (skip.includes(method)) {
        return false;
      }
      const result: Iterable<unknown, unknown> | List<string> | null = Iterable.isIterable(
        validators[method]()
      )
        ? (validators[method]() as Iterable<unknown, unknown>).toList().flatten()
        : validators[method]();
      return result === null ? false : result.some((val) => val !== null);
    });
  }

  hasValidator(method: string): boolean {
    if (!Object.prototype.hasOwnProperty.call(this, method)) {
      throw new Error(`No such validator ${method} found on model.`);
    }
    return true;
  }

  get errors(): Record<string, (pathToProperty?: unknown[]) => string> {
    const validators = this as unknown as Record<string, ValidatorFn>;
    return Object.keys(this).reduce<Record<string, (pathToProperty?: unknown[]) => string>>(
      (obj, method) => {
        const messages = validators[method]() || List(); // Definitively replace `null` with empty list
        obj[method] = (pathToProperty) => deepFind(messages, pathToProperty);
        return obj;
      },
      {}
    );
  }
}
